import { create } from 'zustand';
import { Fleet, Vessel, VesselHistory, VesselHistoryMeteorology } from '@/types/api';
import { subtractDaysFromDate } from '@/utils/datetime';
import useMapsStore from './maps';
import useMapsGLStore from './maps-gl';

interface VesselStore {
  vessels: Array<Vessel>;
  setVessels: (vessels: Array<Vessel>) => void;
  updateVessel: (id: string, data: Vessel) => void;
  selectedVessel: Vessel | null;
  setSelectedVessel: (selectedVessel: Vessel | null) => void;
  selectedVesselHistory: VesselHistory[] | [];
  setSelectedVesselHistory: (selectedVesselHistory: VesselHistory[] | []) => void;
  vesselsSelectedShow: Array<String>;
  setVesselsSelectedShow: (vessels: Array<String>) => void;
  fleets: Array<Fleet>;
  setFleets: (fleets: Array<Fleet>) => void;
  dataDetailVessel: Vessel | null;
  setDataDetailVessel: (dataDetailVessel: Vessel | null) => void;
  dataDetailVesselHistory: VesselHistory | null;
  setDataDetailVesselHistory: (dataDetailVesselHistory: VesselHistory | null) => void;
  withMeteorology: boolean;
  setWithMeteorology: (withMeteorology: boolean) => void;
  dataMeteorology: VesselHistoryMeteorology | null;
  setDataMeteorology: (dataMeteorology: VesselHistoryMeteorology | null) => void;
  closeModalDetailMarker: () => void;
  modalDetailMarker: () => boolean;
  refetch: () => void;
  setRefetch: (refetch: () => void) => void;

  dateRangeHistoryVessel: { start: string; end: string };
  setDateRangeHistoryVessel: (dateRangeHistoryVessel: { start: string; end: string }) => void;
}

const useVesselsStore = create<VesselStore>((set, get) => ({
  vessels: [],
  setVessels: (vessels) =>
    set({ vessels: vessels, vesselsSelectedShow: vessels.map((vessel) => vessel.id) }),
  updateVessel: (id, data) => {
    set((state) => ({
      vessels: state.vessels.map((vessel) => {
        if (vessel.id === id) {
          return { ...vessel, ...data };
        } else {
          return vessel;
        }
      }),
    }));
  },
  selectedVessel: null,
  setSelectedVessel: (selectedVessel) => {
    useMapsStore.getState().cleanEtaPredictionRoute();
    useMapsGLStore.getState().cleanEtaPredictionRoute();
    if (selectedVessel) {
      set({ selectedVessel: selectedVessel, vesselsSelectedShow: [selectedVessel.id] });
    } else {
      set((state) => ({
        selectedVessel: selectedVessel,
        vesselsSelectedShow: state.vessels.map((vessel) => vessel.id),
      }));
    }
  },
  selectedVesselHistory: [],
  setSelectedVesselHistory: (selectedVesselHistory) =>
    set({ selectedVesselHistory: selectedVesselHistory }),
  vesselsSelectedShow: [],
  setVesselsSelectedShow: (vessels) => set({ vesselsSelectedShow: vessels }),
  fleets: [],
  setFleets: (fleets) => set({ fleets: fleets }),
  dataDetailVessel: null,
  setDataDetailVessel: (dataDetailVessel) => set({ dataDetailVessel: dataDetailVessel }),
  dataDetailVesselHistory: null,
  setDataDetailVesselHistory: (dataDetailVesselHistory) =>
    set({ dataDetailVesselHistory: dataDetailVesselHistory }),
  withMeteorology: true,
  setWithMeteorology: (withMeteorology) => set({ withMeteorology: withMeteorology }),
  dataMeteorology: null,
  setDataMeteorology: (dataMeteorology) => set({ dataMeteorology: dataMeteorology }),
  closeModalDetailMarker: () => {
    const {
      setSelectedVessel,
      setWithMeteorology,
      setDataDetailVesselHistory,
      selectedVessel,
      setDateRangeHistoryVessel,
      setVesselsSelectedShow,
      vessels,
    } = get();

    setSelectedVessel(null);
    setWithMeteorology(true);
    /**
     * set clean routes
     */
    useMapsStore.getState().cleanRoutes();
    useMapsGLStore.getState().cleanRoutes();
    useMapsStore.getState().cleanEtaPredictionRoute();
    useMapsGLStore.getState().cleanEtaPredictionRoute();
    setDataDetailVesselHistory(null);

    setVesselsSelectedShow(vessels.map((v) => v.id));
    if (selectedVessel?.latest_receive_date) {
      setDateRangeHistoryVessel({
        start: subtractDaysFromDate(selectedVessel.latest_receive_date, 1, 'days'),
        end: selectedVessel.latest_receive_date,
      });
    }
  },

  modalDetailMarker: () => {
    return get().selectedVessel !== null;
  },
  refetch: () => { },
  setRefetch: (refetch) => set({ refetch: refetch }),
  dateRangeHistoryVessel: { start: '', end: '' },
  setDateRangeHistoryVessel: (dateRangeHistoryVessel) =>
    set(() => ({ dateRangeHistoryVessel: dateRangeHistoryVessel })),
}));

export default useVesselsStore;
